import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import { geocodeAddress, searchAddressSuggestions, reverseGeocode } from '../utils/geocoding';

const DEFAULT_LAT = 6.5244;
const DEFAULT_LNG = 3.3792;

const createMarkerIcon = (color = '#0d6efd') =>
  L.divIcon({
    className: 'custom-marker',
    html: `<div style="background-color:${color};width:24px;height:24px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid white;box-shadow:0 2px 5px rgba(0,0,0,0.3);"></div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 24],
  });

function MapClickHandler({ onMapClick }) {
  useMapEvents({
    click: (e) => onMapClick(e.latlng.lat, e.latlng.lng),
  });
  return null;
}

function MapCenterUpdater({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center && center[0] != null && center[1] != null) {
      map.setView(center, map.getZoom());
    }
  }, [map, center]);
  return null;
}

const MapLocationPicker = ({ initialAddress = '', initialLatitude, initialLongitude, onLocationSelect, className = '' }) => {
  const [address, setAddress] = useState(initialAddress || '');
  const [lat, setLat] = useState(initialLatitude != null ? Number(initialLatitude) : '');
  const [lng, setLng] = useState(initialLongitude != null ? Number(initialLongitude) : '');
  const [geocoding, setGeocoding] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [useMyLocationLoading, setUseMyLocationLoading] = useState(false);
  const debounceRef = useRef(null);

  const effectiveLat = lat !== '' && !Number.isNaN(Number(lat)) ? Number(lat) : DEFAULT_LAT;
  const effectiveLng = lng !== '' && !Number.isNaN(Number(lng)) ? Number(lng) : DEFAULT_LNG;
  const position = [effectiveLat, effectiveLng];

  const notify = useCallback(
    (addr, latitude, longitude) => {
      onLocationSelect?.({
        address: addr != null ? String(addr).trim() || null : null,
        latitude: latitude != null && !Number.isNaN(latitude) ? latitude : effectiveLat,
        longitude: longitude != null && !Number.isNaN(longitude) ? longitude : effectiveLng,
      });
    },
    [onLocationSelect, effectiveLat, effectiveLng]
  );

  useEffect(() => {
    setAddress(initialAddress ? String(initialAddress).trim() : '');
    if (initialLatitude != null && initialLongitude != null) {
      setLat(Number(initialLatitude));
      setLng(Number(initialLongitude));
    }
  }, [initialAddress, initialLatitude, initialLongitude]);

  useEffect(() => {
    notify(address, effectiveLat, effectiveLng);
  }, [address, lat, lng]);

  const handleMarkerDragEnd = useCallback(
    async (e) => {
      const { lat: newLat, lng: newLng } = e.target.getLatLng();
      setLat(newLat);
      setLng(newLng);
      let newAddress = null;
      try {
        const name = await reverseGeocode(newLat, newLng);
        if (name) {
          setAddress(name);
          newAddress = name;
        }
      } catch (_) {}
      notify(newAddress != null ? newAddress : address, newLat, newLng);
    },
    [notify, address]
  );

  const handleMapClick = useCallback(
    async (clickLat, clickLng) => {
      setLat(clickLat);
      setLng(clickLng);
      let newAddress = null;
      try {
        const name = await reverseGeocode(clickLat, clickLng);
        if (name) {
          setAddress(name);
          newAddress = name;
        }
      } catch (_) {}
      notify(newAddress != null ? newAddress : address, clickLat, clickLng);
    },
    [notify, address]
  );

  const handleSearch = async () => {
    if (!address.trim()) return;
    setGeocoding(true);
    setShowSuggestions(false);
    try {
      const result = await geocodeAddress(address);
      if (result) {
        setAddress(result.displayName || address);
        setLat(result.latitude);
        setLng(result.longitude);
      } else {
        alert('Address not found. Drag the pin or tap the map to set the exact location.');
      }
    } catch (e) {
      alert('Address lookup failed. Drag the pin or tap the map to set the location.');
    } finally {
      setGeocoding(false);
    }
  };

  const fetchSuggestions = useCallback(async (query) => {
    const q = String(query || '').trim();
    if (q.length < 2) {
      setSuggestions([]);
      return;
    }
    const results = await searchAddressSuggestions(q, 5);
    setSuggestions(results);
    setShowSuggestions(results.length > 0);
  }, []);

  const onAddressChange = (e) => {
    const text = e.target.value;
    setAddress(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(text), 350);
  };

  const onSelectSuggestion = (item) => {
    setAddress(item.displayName);
    setLat(item.latitude);
    setLng(item.longitude);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser.');
      return;
    }
    setUseMyLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setLat(latitude);
        setLng(longitude);
        const displayName = await reverseGeocode(latitude, longitude);
        if (displayName) setAddress(displayName);
        else setAddress(`${latitude.toFixed(5)}, ${longitude.toFixed(5)}`);
        setUseMyLocationLoading(false);
      },
      (err) => {
        setUseMyLocationLoading(false);
        alert(err.message || 'Could not get your location.');
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 }
    );
  };

  const onLatLngChange = (field, value) => {
    const num = value === '' ? '' : Number(value);
    if (field === 'lat') setLat(num);
    if (field === 'lng') setLng(num);
  };

  return (
    <div className={className}>
      <div className="mb-2 d-flex gap-2 align-items-center">
        <input
          type="text"
          className="form-control"
          value={address}
          onChange={onAddressChange}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          placeholder="Enter address or tap 'Use my location'"
          aria-label="Address"
        />
        <button type="button" className="btn btn-primary" onClick={handleSearch} disabled={geocoding}>
          {geocoding ? (
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
          ) : (
            'Search'
          )}
        </button>
      </div>
      {showSuggestions && suggestions.length > 0 && (
        <div className="list-group mb-2">
          <small className="text-muted px-2">Address suggestions</small>
          {suggestions.map((item, i) => (
            <button
              key={`${item.latitude}-${item.longitude}-${i}`}
              type="button"
              className="list-group-item list-group-item-action list-group-item-sm text-start"
              onClick={() => onSelectSuggestion(item)}
            >
              {item.displayName}
            </button>
          ))}
        </div>
      )}
      <button type="button" className="btn btn-link p-0 mb-2 text-decoration-none" onClick={handleUseMyLocation} disabled={useMyLocationLoading}>
        {useMyLocationLoading ? (
          <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
        ) : null}
        {useMyLocationLoading ? 'Getting location…' : 'Use my current location'}
      </button>
      <p className="small text-muted mb-2">Drag the pin or tap the map to set the exact location. Lat/long update automatically.</p>
      <div className="row g-2 mb-2">
        <div className="col-6">
          <label className="form-label small">Latitude</label>
          <input
            type="number"
            step="any"
            className="form-control form-control-sm"
            value={lat}
            onChange={(e) => onLatLngChange('lat', e.target.value)}
            placeholder="e.g. 6.5244"
          />
        </div>
        <div className="col-6">
          <label className="form-label small">Longitude</label>
          <input
            type="number"
            step="any"
            className="form-control form-control-sm"
            value={lng}
            onChange={(e) => onLatLngChange('lng', e.target.value)}
            placeholder="e.g. 3.3792"
          />
        </div>
      </div>
      <div className="rounded overflow-hidden border" style={{ height: 220, minHeight: 220 }}>
        <MapContainer
          center={position}
          zoom={15}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapCenterUpdater center={position} />
          <MapClickHandler onMapClick={handleMapClick} />
          <Marker
            position={position}
            draggable
            eventHandlers={{ dragend: handleMarkerDragEnd }}
            icon={createMarkerIcon('#0d6efd')}
          />
        </MapContainer>
      </div>
    </div>
  );
};

export default MapLocationPicker;
